import { Lightbulb, Network } from "lucide-react";
import { Link } from "react-router-dom";
import { type RecordingInsights } from "@/lib/api";
import { SourceInsightList } from "@/components/SourceInsightList";
import { entityIcon, entityTypeLabel } from "@/lib/entityTypes";

/**
 * The Insights tab — what was read out of this meeting (INS-1), each with its category, the
 * entities it mentions (INS-2) and its review state. Every state, because the recording
 * detail is one of the two places (with the queue) a pending or discarded insight is
 * legitimately shown — hiding the ones still waiting would misrepresent what the meeting
 * produced.
 */
export function InsightsTab({
  sourceId,
  sourceName,
  insights,
  onViewInSource,
  onApproved,
  onStale,
}: {
  sourceId: string;
  sourceName: string | null;
  insights: RecordingInsights | null;
  onViewInSource: (insightId: string) => void;
  onApproved: () => void;
  onStale: () => void;
}) {
  if (insights === null) {
    return (
      <p className="px-[var(--space-2)] py-[var(--space-4)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Loading…
      </p>
    );
  }

  if (insights.insights.length === 0) {
    return (
      <EmptyTab
        icon={Lightbulb}
        title="No insights yet"
        summary="Once this meeting has been through insight extraction, what it said turns up here — each piece with the words it came from."
      />
    );
  }

  return (
    <SourceInsightList
      sourceId={sourceId}
      sourceName={sourceName}
      insights={insights.insights}
      onViewInSource={onViewInSource}
      onApproved={onApproved}
      onStale={onStale}
    />
  );
}

// ------------------------------------------------------------------ entities (UI-5, M5.2)

/** The Entities tab — the distinct people, products and customers this meeting touched
 * (INS-2), as wrapped pill chips with their type icon. */
export function EntitiesTab({ insights }: { insights: RecordingInsights | null }) {
  if (insights === null) {
    return (
      <p className="px-[var(--space-2)] py-[var(--space-4)] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        Loading…
      </p>
    );
  }

  if (insights.entities.length === 0) {
    return (
      <EmptyTab
        icon={Network}
        title="No entities tagged yet"
        summary="The people, products and customers this meeting mentions appear here once its insights have been tagged."
      />
    );
  }

  return (
    <div className="flex flex-wrap gap-[var(--space-2)]">
      {insights.entities.map((entity) => {
        const Icon = entityIcon(entity.type);

        return (
          <Link
            key={entity.id}
            to={`/entities/${entity.id}`}
            title={entityTypeLabel(entity.type)}
            className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-3)] py-1.5 text-[length:var(--text-sm)] font-medium text-[var(--text-primary)] backdrop-blur-[var(--blur-sm)] transition hover:bg-[var(--surface-2)]"
          >
            <Icon size={13} strokeWidth={2} className="text-[var(--text-muted)]" />
            {entity.name}
          </Link>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ empty state

function EmptyTab({
  icon: Icon,
  title,
  summary,
}: {
  icon: typeof Lightbulb;
  title: string;
  summary: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] bg-[var(--surface-glass)] px-[var(--space-8)] py-[var(--space-12)] text-center backdrop-blur-[var(--blur-sm)]">
      <div className="mb-[var(--space-4)] flex h-12 w-12 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)]">
        <Icon size={22} strokeWidth={2} className="text-[var(--text-muted)]" />
      </div>

      <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">{title}</p>
      <p className="mt-1 max-w-[46ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">{summary}</p>
    </div>
  );
}

// ------------------------------------------------------------------ helpers
